import type { IntlShape } from 'react-intl';

import { TicketStatusType } from '~/enums/TicketStatusType';
import { capitalize } from '~/utils/strings';

export const getStatusLabel = (
  status: TicketStatusType,
  intl: IntlShape,
): string => {
  switch (status) {
    case TicketStatusType.Approved:
      return intl.formatMessage({
        id: 'UseStatusLabel.approved',
        defaultMessage: 'Approved',
        description: 'Status label for approved items',
      });
    case TicketStatusType.Rejected:
      return intl.formatMessage({
        id: 'UseStatusLabel.rejected',
        defaultMessage: 'Rejected',
        description: 'Status label for rejected items',
      });
    case TicketStatusType.Pending:
      return intl.formatMessage({
        id: 'UseStatusLabel.pending',
        defaultMessage: 'Pending',
        description: 'Status label for pending items',
      });
    case TicketStatusType.Done:
      return intl.formatMessage({
        id: 'UseStatusLabel.done',
        defaultMessage: 'Done',
        description: 'Status label for completed items',
      });
    case TicketStatusType.Signed:
      return intl.formatMessage({
        id: 'UseStatusLabel.signed',
        defaultMessage: 'Signed',
        description: 'Status label for signed items',
      });
    case TicketStatusType.SignedViaUpload:
      return intl.formatMessage({
        id: 'UseStatusLabel.signedViaUpload',
        defaultMessage: 'Signed via Upload',
        description: 'Status label for signed items via upload',
      });
    case TicketStatusType.SignatureRequestNotSent:
      return intl.formatMessage({
        id: 'UseStatusLabel.signatureRequestNotSent',
        defaultMessage: 'Signature Request Not Sent',
        description:
          'Status label for items where signature request has not been sent',
      });
    case TicketStatusType.SignaturePending:
      return intl.formatMessage({
        id: 'UseStatusLabel.signaturePending',
        defaultMessage: 'Signature Pending',
        description: 'Status label for items with pending signatures',
      });
    case TicketStatusType.Queued:
      return intl.formatMessage({
        id: 'UseStatusLabel.queued',
        defaultMessage: 'Queued',
        description: 'Status label for queued items',
      });
    case TicketStatusType.EmailDeliveryFailed:
      return intl.formatMessage({
        id: 'UseStatusLabel.emailDeliveryFailed',
        defaultMessage: 'Email Delivery Failed',
        description: 'Status label for items with failed email delivery',
      });
    case TicketStatusType.Cancelled:
      return intl.formatMessage({
        id: 'UseStatusLabel.cancelled',
        defaultMessage: 'Canceled',
        description: 'Status label for canceled items',
      });
    case TicketStatusType.Completed:
      return intl.formatMessage({
        id: 'UseStatusLabel.completed',
        defaultMessage: 'Completed',
        description: 'Status label for completed items',
      });
    case TicketStatusType.Editing:
      return intl.formatMessage({
        id: 'UseStatusLabel.editing',
        defaultMessage: 'Editing',
        description: 'Status label for items that are being edited',
      });
    case TicketStatusType.Review:
      return intl.formatMessage({
        id: 'UseStatusLabel.inReview',
        defaultMessage: 'In Review',
        description: 'Status label for items that are under review',
      });
    case TicketStatusType.PendingCompletion:
      return intl.formatMessage({
        id: 'UseStatusLabel.pendingCompletion',
        defaultMessage: 'Pending Completion',
        description: 'Status label for items pending completion',
      });
    case TicketStatusType.PendingFinalization:
      return intl.formatMessage({
        id: 'UseStatusLabel.pendingFinalization',
        defaultMessage: 'Pending Finalization',
        description: 'Status label for items pending finalization',
      });
    case TicketStatusType.PendingSignatures:
      return intl.formatMessage({
        id: 'UseStatusLabel.pendingSignatures',
        defaultMessage: 'Pending Signatures',
        description: 'Status label for items with pending signatures',
      });
    case TicketStatusType.NotApplicable:
      return intl.formatMessage({
        id: 'UseStatusLabel.notApplicable',
        defaultMessage: 'Not Applicable',
        description: 'Status label for items that are not applicable',
      });
    default:
      return capitalize(status);
  }
};
